// frontend/src/components/staff/AdminStaffAvailabilityDashboard.js
//
// Workforce — SCHED-AVAIL-DASHBOARD-001-B admin "All Availability" matrix
// page. Route: /staff/availability (admin RBAC at App.js level).
//
// SCHED-AVAIL-DASHBOARD-002-B (Sprint 9) — extends the Slice 1 matrix to
// surface one-off (date-specific) availability windows alongside recurring
// patterns when the admin switches the Period picker to "Specific date
// range". Recurring rendering is unchanged from Slice 1; one-off windows
// render with a hollow ring marker + a "only Mon DD" hint line below the
// time range (Zuri spec §2.2 marker vocabulary; §4 period picker; §5
// extended footer breakdown). Slice 1 default-load behavior is preserved
// byte-for-byte — production-safety hard rule.
//
// Visual spec: docs/design/SCHED-AVAIL-DASHBOARD-001-design.md (Slice 1)
//              + docs/design/SCHED-AVAIL-DASHBOARD-002-design.md (Slice 2)
// Story:       docs/backlog/SCHED-AVAIL-DASHBOARD-002-B-fe-matrix-one-off-handling.md
// Tech doc:    docs/architecture/workforce-admin-availability-dashboard-slice-2-tech.md
//
// SCOPE — pure-additive new admin surface. No edits to existing pages.
//
// Production-safety:
//   - Single-revert: removing this route + this component (and its sidebar
//     link insertion) removes the feature.
//   - Read-only consumer of an admin-namespaced GET endpoint that landed in
//     PR #149 (SCHED-AVAIL-DASHBOARD-001-A). No write paths added.
//   - Drill-down reuses SCHED-1K AdminSetAvailabilityModal verbatim
//     (initialView + anchorAvailabilityId props are backward-compatible
//     additions; EditShiftModal callers untouched).
//   - NOT a Customer-Facing Gate (R-020) — admin-internal feature.
//   - All payroll PII is server-stripped per tech doc §"Defense-in-depth
//     payload sanitization"; FE never references those fields.
//
// State management (per tech doc §"State Management (FE — web frontend)"):
//   - Web FE has NO TanStack Query (verified via package.json — codebase
//     convention is plain useState + useEffect + useFocusedPoll). The tech
//     doc's TanStack reference is template; we honor the semantics via the
//     codebase pattern instead: a single `fetchDashboard` keyed on filter
//     state, re-fired on filter change or modal-close success.
//   - Refresh on modal save: the SCHED-1K modal calls
//     `onAvailabilityChanged`, which triggers a re-fetch (AC Scenario 13).
//
// The "matrix" is an HTML <table> per AC Scenario 6 + spec §10 ("Matrix is
// rendered as an HTML <table> (not a CSS grid of divs) so screen readers
// announce headers + row context correctly").

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUserPlus,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSync,
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { workforceAdminAvailabilityDashboardAPI } from "../../services/api";
import AdminSetAvailabilityModal from "./AdminSetAvailabilityModal";
import AdminBulkOneOffAvailabilityModal from "./AdminBulkOneOffAvailabilityModal";
import "./AdminStaffAvailabilityDashboard.css";

// Sun..Sat in US calendar order — spec §3.4.
const DAY_KEYS = ["0", "1", "2", "3", "4", "5", "6"];
const DAY_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_FULL = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Filter chips — spec §3.3. Period picker is non-interactive in v1 (Q3
// lock — only "Typical week" available); we render it greyed out so the
// admin sees the period label without confusion (AC Scenario 3).
const ROLE_OPTIONS = [
  { value: "", label: "All roles" },
  { value: "admin", label: "Admin" },
  { value: "staff", label: "Staff" },
  { value: "massage_therapist", label: "Massage Therapist" },
];

const SORT_OPTIONS = [
  { value: "name_asc", label: "Name A-Z" },
  { value: "name_desc", label: "Name Z-A" },
  { value: "role_name", label: "Role then name" },
  { value: "most_empty", label: "Most empty days first" },
];

// Plural-aware role label for the subtitle dynamic count (AC Scenario 4).
// Falls back to the raw enum string when unknown — we never throw on an
// unfamiliar role value (defense-in-depth — BE may introduce new roles
// without an FE deploy).
const ROLE_PLURALIZE = {
  admin: "admins",
  staff: "staff",
  massage_therapist: "massage therapists",
  front_desk: "front-desk staff",
};

const ROLE_HUMAN = {
  admin: "Admin",
  staff: "Staff",
  massage_therapist: "Massage Therapist",
  front_desk: "Front Desk",
};

const humanRole = (role) => {
  if (!role || typeof role !== "string") return "";
  if (ROLE_HUMAN[role]) return ROLE_HUMAN[role];
  // Title-case unknown role enums for display (massage_therapist -> Massage Therapist).
  return role
    .split("_")
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
};

// Format an HH:MM 24-hour string to a compact 12h MST display. Density
// variant (spec §3.6.1) — when `compact` is true, drops zero-minutes and
// uses lowercase a/p ("9:00 AM" -> "9a"); otherwise full ("9:00 AM").
const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
const formatTimeCell = (hhmm, compact) => {
  if (typeof hhmm !== "string" || !HHMM.test(hhmm)) return hhmm || "";
  const [hRaw, mRaw] = hhmm.split(":");
  const h = parseInt(hRaw, 10);
  const m = parseInt(mRaw, 10);
  const period = h < 12 ? "AM" : "PM";
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  if (compact) {
    const periodCompact = period === "AM" ? "a" : "p";
    if (m === 0) return `${hour12}${periodCompact}`;
    return `${hour12}:${String(m).padStart(2, "0")}${periodCompact}`;
  }
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};

const formatWindowRange = (window, compact) => {
  const start = formatTimeCell(window.startTime, compact);
  const end = formatTimeCell(window.endTime, compact);
  return `${start}-${end}`;
};

// --------- SCHED-AVAIL-DASHBOARD-002-B helpers (Slice 2) -----------------
//
// Format month/day for the Zuri §2.2 date-hint ("only May 24") and §4.3
// popover labels. ASCII-only (no Unicode special chars in production strings).
const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Format YYYY-MM-DD as "May 24" (short month + day-of-month, no year).
const formatMonDay = (ymd) => {
  if (typeof ymd !== "string" || !YMD_PATTERN.test(ymd)) return "";
  const [, mStr, dStr] = ymd.split("-");
  const m = parseInt(mStr, 10);
  const d = parseInt(dStr, 10);
  const mLabel = MONTH_ABBR[m - 1] || "";
  return `${mLabel} ${d}`;
};

// Format YYYY-MM-DD as "May 24, 2026" (for subtitle expansion in Specific
// date range mode per Zuri §4.4).
const formatMonDayYear = (ymd) => {
  if (typeof ymd !== "string" || !YMD_PATTERN.test(ymd)) return "";
  const [yStr, mStr, dStr] = ymd.split("-");
  const m = parseInt(mStr, 10);
  const d = parseInt(dStr, 10);
  const mLabel = MONTH_ABBR[m - 1] || "";
  return `${mLabel} ${d}, ${yStr}`;
};

// Extract YYYY-MM-DD from an Availability window's `effectiveDate` (an ISO
// 8601 string emitted by the BE serializer). Defensive — returns "" when
// the input is missing or malformed.
const ymdFromIso = (iso) => {
  if (typeof iso !== "string") return "";
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  return m ? m[1] : "";
};

// Compute the YYYY-MM-DD of the most recent Sunday in Phoenix-MST relative
// to the supplied `now` Date (defaults to current moment). Matches the BE
// `currentWeekStartPhoenix()` convention in
// workforceAdminAvailabilityDashboardController.js so the FE-computed
// weekStart aligns with the BE's silent fallback.
const phoenixWeekStartYmd = (now) => {
  const base = now instanceof Date ? now : new Date();
  // Phoenix is UTC-7 year-round (no DST). Shift the wall-clock into the
  // Phoenix frame to read the calendar day-of-week and day-of-month.
  const PHX_OFFSET_HOURS = 7;
  const phx = new Date(base.getTime() - PHX_OFFSET_HOURS * 60 * 60 * 1000);
  const dow = phx.getUTCDay();
  // Snap back to Sunday in the Phoenix frame.
  const sundayUtc = Date.UTC(
    phx.getUTCFullYear(),
    phx.getUTCMonth(),
    phx.getUTCDate() - dow,
    0,
    0,
    0,
    0
  );
  const sunday = new Date(sundayUtc);
  // Emit as YYYY-MM-DD using UTC getters (the snapped instant IS the
  // Phoenix-MST 00:00 wall-clock Sunday).
  const yyyy = sunday.getUTCFullYear();
  const mm = String(sunday.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(sunday.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Add `days` to a YYYY-MM-DD (Phoenix-MST calendar). Returns a new
// YYYY-MM-DD. Used to compute the week-end label + popover quick presets.
const addDaysYmd = (ymd, days) => {
  if (typeof ymd !== "string" || !YMD_PATTERN.test(ymd)) return ymd;
  const [yStr, mStr, dStr] = ymd.split("-");
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10);
  const d = parseInt(dStr, 10);
  const base = new Date(Date.UTC(y, m - 1, d, 12, 0, 0, 0));
  base.setUTCDate(base.getUTCDate() + days);
  const yyyy = base.getUTCFullYear();
  const mm = String(base.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(base.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Map a window's effectiveDate (ISO) into a "only Mon DD" date-hint line
// per Zuri §2.2. Empty string when the window has no effectiveDate (which
// shouldn't happen for one-offs but is defensive).
const oneOffHint = (window) => {
  if (!window || window.isRecurring !== false) return "";
  const ymd = ymdFromIso(window.effectiveDate);
  if (!ymd) return "";
  return `only ${formatMonDay(ymd)}`;
};

// Convert a YYYY-MM-DD into an aria-label-friendly "Saturday May 24"
// representation (used in cell aria-labels per Zuri §2.4).
const formatDayMonDay = (ymd) => {
  if (typeof ymd !== "string" || !YMD_PATTERN.test(ymd)) return "";
  const [yStr, mStr, dStr] = ymd.split("-");
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10);
  const d = parseInt(dStr, 10);
  // 19:00 UTC == 12:00 Phoenix-MST same calendar date; getUTCDay() === Phoenix dow.
  const dt = new Date(Date.UTC(y, m - 1, d, 19, 0, 0, 0));
  if (isNaN(dt.getTime())) return "";
  return `${DAY_FULL[dt.getUTCDay()]} ${MONTH_ABBR[m - 1]} ${d}`;
};

// Compute the YYYY-MM-DD for a given dayOfWeek (0..6) within a given
// weekStart (Sunday YYYY-MM-DD). Used to seed the modal's `effectiveDate`
// prefill when admin clicks an empty cell in Specific date range mode.
const ymdForDayInWeek = (weekStartYmd, dayOfWeek) => {
  if (
    typeof weekStartYmd !== "string" ||
    !YMD_PATTERN.test(weekStartYmd) ||
    typeof dayOfWeek !== "number" ||
    dayOfWeek < 0 ||
    dayOfWeek > 6
  ) {
    return null;
  }
  return addDaysYmd(weekStartYmd, dayOfWeek);
};

// Order windows in a single day cell: recurring first (by startTime ASC),
// then one-off (by effectiveDate ASC, tiebreak by startTime ASC).
// Per Zuri §2.2 "Stacking order: All recurring windows first, then all
// one-off windows."
//
// Defensive — the BE in Slice 2 emits one-off first then recurring; the FE
// re-orders so the UX rule lives in one place. Re-sorting an already-sorted
// short array is cheap (typical 1-5 windows per cell).
const orderWindowsForCell = (windows) => {
  if (!Array.isArray(windows) || windows.length <= 1) {
    return Array.isArray(windows) ? windows : [];
  }
  const copy = [...windows];
  copy.sort((a, b) => {
    const aRecurring = a && a.isRecurring !== false;
    const bRecurring = b && b.isRecurring !== false;
    if (aRecurring !== bRecurring) {
      return aRecurring ? -1 : 1;
    }
    if (!aRecurring) {
      const aIso = a.effectiveDate || "";
      const bIso = b.effectiveDate || "";
      if (aIso < bIso) return -1;
      if (aIso > bIso) return 1;
    }
    const at = a.startTime || "";
    const bt = b.startTime || "";
    if (at < bt) return -1;
    if (at > bt) return 1;
    return 0;
  });
  return copy;
};

// Walk a staff row's matrix to count recurring vs one-off windows.
const countWindowKinds = (row) => {
  let recurring = 0;
  let oneOff = 0;
  if (!row || !row.availability) return { recurring, oneOff };
  for (const k of DAY_KEYS) {
    const arr = row.availability[k];
    if (!Array.isArray(arr)) continue;
    for (const w of arr) {
      if (w && w.isRecurring === false) oneOff += 1;
      else if (w) recurring += 1;
    }
  }
  return { recurring, oneOff };
};

// Period picker modes (Zuri §4.2).
const PERIOD_TYPICAL = "typical";
const PERIOD_SPECIFIC = "specific";

// Period popover validation rules (Zuri §4.3).
const PERIOD_MAX_RANGE_DAYS = 31;

const diffDaysYmd = (startYmd, endYmd) => {
  if (
    typeof startYmd !== "string" ||
    typeof endYmd !== "string" ||
    !YMD_PATTERN.test(startYmd) ||
    !YMD_PATTERN.test(endYmd)
  ) {
    return null;
  }
  const [sy, sm, sd] = startYmd.split("-").map((x) => parseInt(x, 10));
  const [ey, em, ed] = endYmd.split("-").map((x) => parseInt(x, 10));
  const start = Date.UTC(sy, sm - 1, sd, 12);
  const end = Date.UTC(ey, em - 1, ed, 12);
  return Math.round((end - start) / (24 * 60 * 60 * 1000));
};

// Compute today's day-of-week in America/Phoenix (MST, no DST). We avoid a
// moment-timezone dependency by leveraging Intl with the IANA zone; falls
// back to the local-tz day-of-week if Intl is missing (jsdom test envs do
// support Intl.DateTimeFormat options, but defensive).
const todayDayOfWeekMst = () => {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Phoenix",
      weekday: "short",
    });
    const short = fmt.format(new Date()); // "Sun", "Mon", ...
    const idx = DAY_HEADERS.indexOf(short);
    if (idx >= 0) return idx;
  } catch (e) {
    /* fall through */
  }
  return new Date().getDay();
};

// Count how many of the seven day buckets have ZERO windows. Used by the
// "Most empty days first" sort + footer summary.
const countEmptyDays = (availability) => {
  if (!availability || typeof availability !== "object") return 7;
  let empties = 0;
  for (const k of DAY_KEYS) {
    const arr = availability[k];
    if (!Array.isArray(arr) || arr.length === 0) empties += 1;
  }
  return empties;
};

const hasAnyAvailability = (row) => countEmptyDays(row.availability) < 7;

// Sort rows in place per the selected sort key. Returns a new array.
const sortRows = (rows, sortKey) => {
  const copy = Array.isArray(rows) ? [...rows] : [];
  const byName = (a, b) =>
    (a.displayName || "").localeCompare(b.displayName || "");
  switch (sortKey) {
    case "name_desc":
      copy.sort((a, b) => byName(b, a));
      break;
    case "role_name":
      copy.sort((a, b) => {
        const ra = (a.primaryRole || "").localeCompare(b.primaryRole || "");
        return ra !== 0 ? ra : byName(a, b);
      });
      break;
    case "most_empty":
      copy.sort((a, b) => {
        const ea = countEmptyDays(a.availability);
        const eb = countEmptyDays(b.availability);
        if (ea !== eb) return eb - ea; // descending
        return byName(a, b);
      });
      break;
    case "name_asc":
    default:
      copy.sort(byName);
  }
  return copy;
};

// Default filter state — passed through to the API helper which drops
// empty / default values from the query string so the BE applies its
// defaults (active=true, no role filter, includeOneOff=false).
//
// SCHED-AVAIL-DASHBOARD-002-B adds two period-related fields:
//   - periodMode: "typical" | "specific"  — typical = Slice 1 (recurring-
//                                            only); specific = Slice 2
//                                            (recurring + one-off in week)
//   - weekStart: "YYYY-MM-DD" | null      — Phoenix-MST Sunday; only used
//                                            when periodMode === 'specific'
const DEFAULT_FILTERS = {
  primaryRole: "",
  onlyNoAvailability: false,
  // SCHED-AVAIL-DASHBOARD-003-B story Scenarios 9 + 11 — new filter +
  // mutual-exclusion with onlyNoAvailability.
  onlyOneOffWindows: false,
  sort: "name_asc",
  periodMode: PERIOD_TYPICAL,
  weekStart: null,
};

const isAnyFilterNonDefault = (f) =>
  (typeof f.primaryRole === "string" && f.primaryRole.length > 0) ||
  f.onlyNoAvailability === true ||
  f.onlyOneOffWindows === true ||
  (typeof f.sort === "string" && f.sort !== DEFAULT_FILTERS.sort) ||
  (typeof f.periodMode === "string" && f.periodMode !== PERIOD_TYPICAL);

// SCHED-AVAIL-DASHBOARD-003-B Scenario 9 — count one-off windows for a staff
// row. Returns true if the row has at least one `isRecurring === false`
// window across any day slot. Pure helper for the new "Only one-off" filter.
const rowHasAnyOneOff = (row) => {
  if (!row || !row.availability || typeof row.availability !== "object") {
    return false;
  }
  for (const k of DAY_KEYS) {
    const arr = row.availability[k];
    if (!Array.isArray(arr)) continue;
    for (const w of arr) {
      if (w && w.isRecurring === false) return true;
    }
  }
  return false;
};

const AdminStaffAvailabilityDashboard = () => {
  const navigate = useNavigate();

  // Filter state — combined into one object so a single setter resets the
  // whole strip on "Clear filters" click.
  const [filters, setFilters] = React.useState(DEFAULT_FILTERS);

  // Data state. `generatedAt` is rendered as the page "refreshed Xs ago"
  // hint (tech doc §"Error Handling Strategy" "stale snapshot mid-edit").
  const [staffRows, setStaffRows] = React.useState([]);
  const [generatedAt, setGeneratedAt] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  // SCHED-AVAIL-DASHBOARD-002-C — distinguish initial mount from subsequent
  // refetches. Initial mount keeps the Slice 1 full-skeleton replacement so
  // the very-first paint isn't a half-rendered chrome over a blank matrix.
  // Subsequent fetches (manual refresh, filter change, period chevrons,
  // modal-save callback) instead render an overlay over the matrix area —
  // chrome (title, filter strip, footer) stays put and does not flicker per
  // story Scenarios 2 + 6.
  const hasLoadedOnceRef = React.useRef(false);

  // Today's column for the orientation gold-border (spec §3.4).
  const [todayDow, setTodayDow] = React.useState(() => todayDayOfWeekMst());
  React.useEffect(() => {
    // Refresh "today" once on mount and on a midnight rollover. Cheap.
    setTodayDow(todayDayOfWeekMst());
  }, []);

  // Modal state — opened by cell / staff-label clicks.
  const [modalState, setModalState] = React.useState(null); // { userId, staffFullName, prefill, initialView, anchorAvailabilityId }

  // SCHED-AVAIL-DASHBOARD-003-B — bulk one-off modal. Single-staff x
  // N-windows per Marcus's tech doc and BE PR #168. `preselectedUserId`
  // is null when admin enters bulk mode from the filter-strip CTA;
  // populated when admin clicks a row's bulk-launch affordance (future
  // enhancement — keep the prop ready).
  const [bulkModalOpen, setBulkModalOpen] = React.useState(false);
  const [bulkPreselectedUserId, setBulkPreselectedUserId] =
    React.useState(null);

  // SCHED-AVAIL-DASHBOARD-002-B — Period picker dropdown + popover state.
  // Dropdown menu surfaces the two-mode choice (Typical / Specific). Popover
  // collects the YYYY-MM-DD week-start when admin picks Specific. Both are
  // ephemeral UI bits — they do not persist across renders beyond filters.*.
  const [periodMenuOpen, setPeriodMenuOpen] = React.useState(false);
  const [periodPopoverOpen, setPeriodPopoverOpen] = React.useState(false);
  const [periodDraft, setPeriodDraft] = React.useState(() => ({
    weekStart: phoenixWeekStartYmd(),
    error: "",
  }));

  // Density variant — toggled below the 1280px viewport per spec §3.6.1.
  const [compact, setCompact] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 1280;
  });
  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setCompact(window.innerWidth < 1280);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ---- Fetch the dashboard -----------------------------------------------
  // Single effect keyed on the server-side filters (active default, primaryRole,
  // and — Slice 2 — includeOneOff + weekStart when in Specific date range mode).
  // The client-side filters (onlyNoAvailability, sort) do NOT re-fetch — they
  // apply in-memory to the already-loaded staffRows.
  //
  // SCHED-AVAIL-DASHBOARD-002-B: when periodMode === 'specific', the request
  // adds `includeOneOff=true` + `weekStart=YYYY-MM-DD` so the BE folds one-off
  // rows into the matrix. typical mode is byte-identical to Slice 1 — single-
  // revert of this story removes Slice 2 behavior without touching the Slice 1
  // contract.
  const fetchDashboard = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const includeOneOff = filters.periodMode === PERIOD_SPECIFIC;
      const args = {
        primaryRole: filters.primaryRole || undefined,
      };
      if (includeOneOff) {
        args.includeOneOff = true;
        if (filters.weekStart) {
          args.weekStart = filters.weekStart;
        }
      }
      const res = await workforceAdminAvailabilityDashboardAPI.list(args);
      const data = res?.data || {};
      const rows = Array.isArray(data.staff) ? data.staff : [];
      setStaffRows(rows);
      setGeneratedAt(
        typeof data.generatedAt === "string" ? data.generatedAt : null
      );
      // First successful fetch flips us out of "initial mount" mode so future
      // re-fetches render the chrome-preserving overlay instead of the full
      // skeleton replacement (story Scenarios 2 + 6).
      hasLoadedOnceRef.current = true;
    } catch (err) {
      setError(
        "Couldn't load staff availability. Something went wrong on our end."
      );
      try {
        if (typeof toast === "function") {
          toast("Could not load staff availability — try again.", {
            autoClose: 4000,
          });
        } else if (toast && typeof toast.error === "function") {
          toast.error("Could not load staff availability — try again.", {
            autoClose: 4000,
          });
        }
      } catch (_e) {
        /* toast container absence is OK */
      }
    } finally {
      setLoading(false);
    }
  }, [filters.primaryRole, filters.periodMode, filters.weekStart]);

  React.useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // ---- In-memory filter + sort -------------------------------------------
  // SCHED-AVAIL-DASHBOARD-003-B story Scenarios 9-11:
  //   - "Only one-off windows" filter (Scenario 9): keep rows with >=1 one-off
  //   - Existing "Only no availability" filter (Scenario 10): keep empty rows
  //   - Mutually exclusive (Scenario 11): only one can be true at a time; we
  //     enforce this at the toggle handler. Defensive: if both flags are
  //     somehow true, treat "Only no availability" as the stronger filter
  //     (zero-availability is a strict subset of "no one-off").
  const visibleRows = React.useMemo(() => {
    let rows = Array.isArray(staffRows) ? [...staffRows] : [];
    if (filters.onlyNoAvailability) {
      rows = rows.filter((r) => !hasAnyAvailability(r));
    } else if (filters.onlyOneOffWindows) {
      rows = rows.filter(rowHasAnyOneOff);
    }
    rows = sortRows(rows, filters.sort);
    return rows;
  }, [
    staffRows,
    filters.onlyNoAvailability,
    filters.onlyOneOffWindows,
    filters.sort,
  ]);

  const totalWithAvailability = React.useMemo(
    () => visibleRows.filter(hasAnyAvailability).length,
    [visibleRows]
  );
  const totalWithoutAvailability = visibleRows.length - totalWithAvailability;

  // ---- Subtitle dynamic count --------------------------------------------
  // "All staff · typical week · MST" when no role filter; switches to
  // "N <role-plural> · typical week · MST" when role-filtered (Scenario 4).
  // Slice 2: in Specific date range mode the period label expands to
  // "May 17 - May 23, 2026 (specific dates)" per Zuri §4.4.
  const subtitle = React.useMemo(() => {
    const mstSuffix = "times shown in MST";
    let period = "typical week (recurring patterns)";
    if (filters.periodMode === PERIOD_SPECIFIC && filters.weekStart) {
      const start = filters.weekStart;
      const end = addDaysYmd(start, 6);
      period = `${formatMonDay(start)} - ${formatMonDayYear(end)} (specific dates)`;
    }
    if (!filters.primaryRole) {
      return `All staff · ${period} · ${mstSuffix}`;
    }
    const plural =
      ROLE_PLURALIZE[filters.primaryRole] ||
      `${filters.primaryRole.replace(/_/g, " ")}s`;
    return `${visibleRows.length} ${plural} · ${period} · ${mstSuffix}`;
  }, [
    filters.primaryRole,
    filters.periodMode,
    filters.weekStart,
    visibleRows.length,
  ]);

  // ---- Filter handlers ---------------------------------------------------
  const updateFilter = (patch) =>
    setFilters((prev) => ({ ...prev, ...patch }));
  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPeriodMenuOpen(false);
    setPeriodPopoverOpen(false);
    setPeriodDraft({ weekStart: phoenixWeekStartYmd(), error: "" });
  };
  const filtersDirty = isAnyFilterNonDefault(filters);

  // SCHED-AVAIL-DASHBOARD-003-B Scenario 11 — mutually-exclusive filter
  // toggles. Toggling "Only no availability" ON clears "Only one-off windows"
  // and vice-versa. The unchecked-from-active state simply clears the flag.
  const toggleOnlyNoAvailability = (next) => {
    if (next) {
      setFilters((prev) => ({
        ...prev,
        onlyNoAvailability: true,
        onlyOneOffWindows: false,
      }));
    } else {
      setFilters((prev) => ({ ...prev, onlyNoAvailability: false }));
    }
  };
  const toggleOnlyOneOffWindows = (next) => {
    if (next) {
      setFilters((prev) => ({
        ...prev,
        onlyOneOffWindows: true,
        onlyNoAvailability: false,
      }));
    } else {
      setFilters((prev) => ({ ...prev, onlyOneOffWindows: false }));
    }
  };

  // SCHED-AVAIL-DASHBOARD-003-B — Bulk modal lifecycle.
  const openBulkModal = (preselectedUserId = null) => {
    setBulkPreselectedUserId(preselectedUserId);
    setBulkModalOpen(true);
  };
  const closeBulkModal = () => {
    setBulkModalOpen(false);
    setBulkPreselectedUserId(null);
  };
  const onBulkSubmitSuccess = () => {
    // Re-fetch the dashboard so the new one-off windows render in the
    // matrix (story Scenario 3). Modal handles its own close via onClose.
    fetchDashboard();
  };

  // SCHED-AVAIL-DASHBOARD-002-B Period picker handlers ---------------------
  // The picker is a dropdown chip with two options. Selecting "Typical
  // week" reverts to Slice 1 fetch. Selecting "Specific date range" opens
  // the popover with the current Phoenix-MST Sunday pre-populated; admin
  // confirms or picks a different week.
  const openPeriodMenu = () => setPeriodMenuOpen((v) => !v);
  const selectPeriodTypical = () => {
    setPeriodMenuOpen(false);
    setPeriodPopoverOpen(false);
    updateFilter({ periodMode: PERIOD_TYPICAL, weekStart: null });
    setPeriodDraft({ weekStart: phoenixWeekStartYmd(), error: "" });
  };
  const selectPeriodSpecific = () => {
    setPeriodMenuOpen(false);
    const seed =
      filters.weekStart && YMD_PATTERN.test(filters.weekStart)
        ? filters.weekStart
        : phoenixWeekStartYmd();
    setPeriodDraft({ weekStart: seed, error: "" });
    setPeriodPopoverOpen(true);
  };
  const closePeriodPopover = () => setPeriodPopoverOpen(false);

  // Validation per Zuri §4.3 — currently the popover collects a single
  // weekStart (Sunday) and we derive end = weekStart + 6. Range max = 7
  // days (full week). Min start = 90 days ago; max start = 365 days out.
  const validateDraft = (draft) => {
    const weekStart = draft && draft.weekStart;
    if (typeof weekStart !== "string" || !YMD_PATTERN.test(weekStart)) {
      return "Pick a valid week-start date.";
    }
    const todayYmdStr = phoenixWeekStartYmd();
    // Allow up to 90 days backward and 365 days forward from "this week".
    const backDelta = diffDaysYmd(weekStart, todayYmdStr);
    if (backDelta != null && backDelta > 90) {
      return "Week start cannot be more than 90 days in the past.";
    }
    const forwardDelta = diffDaysYmd(todayYmdStr, weekStart);
    if (forwardDelta != null && forwardDelta > 365) {
      return "Week start cannot be more than 365 days in the future.";
    }
    return "";
  };

  const applyPeriodDraft = () => {
    const err = validateDraft(periodDraft);
    if (err) {
      setPeriodDraft((prev) => ({ ...prev, error: err }));
      return;
    }
    updateFilter({
      periodMode: PERIOD_SPECIFIC,
      weekStart: periodDraft.weekStart,
    });
    setPeriodPopoverOpen(false);
  };

  const setDraftWeekStart = (ymd) => {
    setPeriodDraft({ weekStart: ymd, error: "" });
  };

  // Quick presets per Zuri §4.3 — "This week" / "Next week" / "Previous
  // week". They write into the draft, not directly into filters, so admin
  // can confirm via Apply.
  const presetThisWeek = () =>
    setDraftWeekStart(phoenixWeekStartYmd());
  const presetNextWeek = () =>
    setDraftWeekStart(addDaysYmd(phoenixWeekStartYmd(), 7));
  const presetPrevWeek = () =>
    setDraftWeekStart(addDaysYmd(phoenixWeekStartYmd(), -7));

  // SCHED-AVAIL-DASHBOARD-002-C — adjacent prev/next week chevrons in the
  // filter strip when admin is in Specific date range mode. Story Scenario 5
  // wants "scroll to the previous/next week via the period picker chevrons"
  // to update the subtitle and re-fetch — these write directly to
  // `filters.weekStart` (bypassing the popover) so chevron clicks feel
  // immediate. The fetch fires automatically via the `fetchDashboard` effect
  // dependency on `filters.weekStart`.
  const shiftWeek = (days) => {
    if (filters.periodMode !== PERIOD_SPECIFIC) return;
    const base =
      filters.weekStart && YMD_PATTERN.test(filters.weekStart)
        ? filters.weekStart
        : phoenixWeekStartYmd();
    const next = addDaysYmd(base, days);
    if (!YMD_PATTERN.test(next)) return;
    updateFilter({ weekStart: next });
  };
  const shiftWeekPrev = () => shiftWeek(-7);
  const shiftWeekNext = () => shiftWeek(7);

  // ---- Drill-down handlers (AC Scenarios 10 / 11 / 12 / 9 banner) -------
  const openListForStaff = (row) => {
    setModalState({
      userId: row.userId,
      staffFullName: row.displayName,
      prefill: null,
      initialView: "list",
      anchorAvailabilityId: null,
    });
  };

  // SCHED-AVAIL-DASHBOARD-002-B — empty-cell click contract:
  //   - Typical mode: open CREATE pre-set to recurring (Slice 1 behavior).
  //   - Specific mode: open CREATE pre-set to one-off with `effectiveDate`
  //     === the YYYY-MM-DD of that column-day in the current week (AC
  //     Scenario 6). dayOfWeek is implicit (derived from the date by the
  //     modal helper).
  const openCreateForCell = (row, dayOfWeek) => {
    if (
      filters.periodMode === PERIOD_SPECIFIC &&
      filters.weekStart &&
      YMD_PATTERN.test(filters.weekStart)
    ) {
      const ymd = ymdForDayInWeek(filters.weekStart, dayOfWeek);
      if (ymd) {
        setModalState({
          userId: row.userId,
          staffFullName: row.displayName,
          prefill: {
            isRecurring: false,
            effectiveDate: ymd,
            dayOfWeek,
          },
          initialView: "create",
          anchorAvailabilityId: null,
        });
        return;
      }
    }
    setModalState({
      userId: row.userId,
      staffFullName: row.displayName,
      prefill: { dayOfWeek },
      initialView: "create",
      anchorAvailabilityId: null,
    });
  };

  // Filled-window click contract — uniform across Slice 1 + Slice 2:
  // open LIST mode anchored to the targeted window. Per AC Scenario 5
  // "behavior consistent with Slice 1 §3.7" — admin still clicks Edit
  // explicitly inside the modal to begin mutation. The LIST row attribution
  // ("Recurring" vs "Single occurrence · [date]") communicates the kind.
  const openListAnchored = (row, windowId) => {
    setModalState({
      userId: row.userId,
      staffFullName: row.displayName,
      prefill: null,
      initialView: "list",
      anchorAvailabilityId: windowId,
    });
  };
  const closeModal = () => setModalState(null);

  // ---- Render helpers ----------------------------------------------------
  const renderCell = (row, dayOfWeek) => {
    const rawWindows = (row.availability && row.availability[String(dayOfWeek)]) || [];
    const windows = orderWindowsForCell(rawWindows);
    const isToday = dayOfWeek === todayDow;
    const dayName = DAY_FULL[dayOfWeek];
    const cellBaseClass =
      "aad__cell" + (isToday ? " aad__cell--today" : "");
    if (windows.length === 0) {
      const ariaLabel = `No availability set for ${row.displayName} on ${dayName}. Click to create.`;
      return (
        <td
          key={dayOfWeek}
          className={cellBaseClass + " aad__cell--empty"}
          data-day={dayOfWeek}
          data-testid={`aad-cell-${row.userId}-${dayOfWeek}`}
        >
          <button
            type="button"
            className="aad__cell-empty-btn"
            onClick={() => openCreateForCell(row, dayOfWeek)}
            aria-label={ariaLabel}
          >
            <span aria-hidden="true">{"—"}</span>
          </button>
        </td>
      );
    }
    return (
      <td
        key={dayOfWeek}
        className={cellBaseClass + " aad__cell--filled"}
        data-day={dayOfWeek}
        data-testid={`aad-cell-${row.userId}-${dayOfWeek}`}
      >
        <ul className="aad__cell-windows">
          {windows.map((w) => {
            const isOneOff = w.isRecurring === false;
            const range = formatWindowRange(w, compact);
            // Slice 2 aria-label distinguishes recurring vs one-off per
            // Zuri §2.4. Recurring keeps the Slice 1 phrasing exactly
            // (back-compat with any aria-driven Nia / accessibility tests).
            const ymd = isOneOff ? ymdFromIso(w.effectiveDate) : "";
            const ariaLabel = isOneOff
              ? `One-off availability for ${row.displayName} on ${formatDayMonDay(
                  ymd
                )}: ${formatWindowRange(w, false)}${
                  w.setByAdmin ? ", set by admin" : ""
                }`
              : `${row.displayName} availability for ${dayName}: ${formatWindowRange(
                  w,
                  false
                )}${w.setByAdmin ? ", set by admin" : ""}`;
            const tooltip = isOneOff
              ? `Single occurrence - ${formatMonDayYear(ymd)} - ${formatWindowRange(
                  w,
                  false
                )} (MST)${w.setByAdmin ? " - Set by admin" : ""}`
              : `Recurring weekly - ${dayName}s ${formatWindowRange(
                  w,
                  false
                )} (MST)${w.setByAdmin ? " - Set by admin" : ""}`;
            return (
              <li
                key={w._id}
                className={
                  "aad__cell-window" +
                  (isOneOff ? " aad__cell-window--one-off" : " aad__cell-window--recurring")
                }
                data-window-kind={isOneOff ? "one-off" : "recurring"}
              >
                <button
                  type="button"
                  className="aad__cell-window-btn"
                  onClick={() => openListAnchored(row, w._id)}
                  aria-label={ariaLabel}
                  title={tooltip}
                  data-testid={`aad-window-${row.userId}-${dayOfWeek}-${w._id}`}
                >
                  <span
                    className={
                      "aad__marker" +
                      (isOneOff ? " aad__marker--one-off" : " aad__marker--recurring")
                    }
                    aria-hidden="true"
                    data-testid={`aad-marker-${w._id}`}
                  />
                  <span className="aad__cell-window-range">{range}</span>
                  {w.setByAdmin ? (
                    <span
                      className="aad__badge-admin"
                      title="Set by admin"
                      aria-label="Set by admin"
                    >
                      A
                    </span>
                  ) : null}
                </button>
                {isOneOff && ymd ? (
                  <p
                    className="aad__cell-window-hint"
                    data-testid={`aad-window-hint-${w._id}`}
                    aria-hidden="true"
                  >
                    {oneOffHint(w)}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      </td>
    );
  };

  const renderStaffRow = (row) => {
    const isEmptyRow = !hasAnyAvailability(row);
    const labelAria = `Manage availability for ${row.displayName}`;
    return (
      <tr key={row.userId} className="aad__row" data-testid={`aad-row-${row.userId}`}>
        <th scope="row" className="aad__row-label-cell">
          <button
            type="button"
            className="aad__row-label-btn"
            onClick={() => openListForStaff(row)}
            aria-label={labelAria}
            data-testid={`aad-staff-label-${row.userId}`}
          >
            <span className="aad__row-label-name">{row.displayName}</span>
            <span className="aad__row-label-role">
              {humanRole(row.primaryRole)}
            </span>
          </button>
        </th>
        {isEmptyRow ? (
          <td
            colSpan={7}
            className="aad__row-banner"
            data-testid={`aad-empty-row-${row.userId}`}
          >
            <button
              type="button"
              className="aad__row-banner-btn"
              onClick={() => openCreateForCell(row, todayDow)}
              aria-label={`No availability set for ${row.displayName} — click to set.`}
            >
              No availability set yet — click to set
            </button>
          </td>
        ) : (
          [0, 1, 2, 3, 4, 5, 6].map((d) => renderCell(row, d))
        )}
      </tr>
    );
  };

  const renderEmptyStateNoStaff = () => (
    <div
      className="aad__empty-state"
      role="status"
      data-testid="aad-empty-zero-staff"
    >
      <FaUserPlus className="aad__empty-state-icon" aria-hidden="true" />
      <h2 className="aad__empty-state-title">No staff members yet</h2>
      <p className="aad__empty-state-body">
        Add staff in Staff Profiles to start tracking availability.
      </p>
      <button
        type="button"
        className="aad__cta-primary"
        onClick={() => navigate("/staff/staff-profiles")}
      >
        Open Staff Profiles
      </button>
    </div>
  );

  const renderEmptyStateNoMatch = () => (
    <div
      className="aad__empty-state aad__empty-state--success"
      role="status"
      data-testid="aad-empty-no-match"
    >
      <FaCheckCircle className="aad__empty-state-icon aad__empty-state-icon--success" aria-hidden="true" />
      <h2 className="aad__empty-state-title">No staff match this filter</h2>
      <p className="aad__empty-state-body">
        All your staff have availability set. Try clearing filters to see the
        full matrix.
      </p>
      <button
        type="button"
        className="aad__cta-secondary"
        onClick={clearFilters}
      >
        Clear filters
      </button>
    </div>
  );

  const renderErrorState = () => (
    <div
      className="aad__empty-state aad__empty-state--error"
      role="alert"
      data-testid="aad-error-state"
    >
      <FaExclamationTriangle
        className="aad__empty-state-icon aad__empty-state-icon--error"
        aria-hidden="true"
      />
      <h2 className="aad__empty-state-title">Couldn{"’"}t load availability</h2>
      <p className="aad__empty-state-body">
        Something went wrong on our end. Try again in a moment.
      </p>
      <button
        type="button"
        className="aad__cta-primary"
        onClick={fetchDashboard}
      >
        Try again
      </button>
    </div>
  );

  const renderSkeleton = () => (
    <div
      className="aad__skeleton"
      role="status"
      aria-label="Loading staff availability"
      data-testid="aad-skeleton"
    >
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <div key={i} className="aad__skeleton-row" />
      ))}
    </div>
  );

  // Footer summary (AC Scenario 17 carried; Slice 2 §5 extended breakdown
  // in Specific date range mode). The Typical mode label stays exactly as
  // Slice 1 — back-compat.
  const oneOffStaffCount = React.useMemo(() => {
    let count = 0;
    for (const r of visibleRows) {
      const { oneOff } = countWindowKinds(r);
      if (oneOff > 0) count += 1;
    }
    return count;
  }, [visibleRows]);

  const recurringStaffCount = React.useMemo(() => {
    let count = 0;
    for (const r of visibleRows) {
      const { recurring } = countWindowKinds(r);
      if (recurring > 0) count += 1;
    }
    return count;
  }, [visibleRows]);

  let footerLabel;
  if (filters.onlyNoAvailability) {
    footerLabel = `Showing ${visibleRows.length} staff with no availability`;
  } else if (filters.periodMode === PERIOD_SPECIFIC) {
    footerLabel =
      `Showing ${visibleRows.length} staff · ${totalWithAvailability} with availability ` +
      `(${recurringStaffCount} recurring, ${oneOffStaffCount} with one-offs) · ${totalWithoutAvailability} with none`;
  } else {
    footerLabel = `Showing ${visibleRows.length} staff · ${totalWithAvailability} with availability set · ${totalWithoutAvailability} with none`;
  }

  return (
    <div className="aad" data-testid="aad-page">
      {/* ADMIN MODE chrome bar (spec §3.2) -------------------------------- */}
      <div
        className="aad__admin-mode-bar"
        role="note"
        aria-label="Admin staff availability view"
        data-testid="aad-admin-mode-bar"
      >
        <span className="aad__admin-mode-text">ADMIN MODE</span>
      </div>

      {/* Page title block ----------------------------------------------- */}
      <header className="aad__header">
        <h1 className="aad__h1">Staff Availability</h1>
        <p className="aad__subtitle" data-testid="aad-subtitle">
          {subtitle}
        </p>
      </header>

      {/* Filter strip --------------------------------------------------- */}
      <div className="aad__filter-strip" role="group" aria-label="Matrix filters">
        <label className="aad__chip aad__chip--select">
          <span className="aad__chip-label">Role</span>
          <select
            className="aad__chip-control"
            value={filters.primaryRole}
            onChange={(e) => updateFilter({ primaryRole: e.target.value })}
            data-testid="aad-filter-role"
            aria-label="Filter by role"
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        {/* SCHED-AVAIL-DASHBOARD-002-B — interactive Period picker. Default
            "Typical week" preserves Slice 1 behavior (recurring-only fetch).
            Picking "Specific date range" opens the popover (Zuri §4.3) and
            on Apply re-fetches with includeOneOff=true + weekStart. */}
        <div className="aad__period-picker-wrap">
          <button
            type="button"
            className={
              "aad__chip aad__chip--dropdown" +
              (filters.periodMode === PERIOD_SPECIFIC
                ? " aad__chip--active"
                : "")
            }
            onClick={openPeriodMenu}
            data-testid="aad-filter-period"
            aria-haspopup="menu"
            aria-expanded={periodMenuOpen ? "true" : "false"}
            aria-label={
              filters.periodMode === PERIOD_SPECIFIC
                ? `Period: ${formatMonDay(filters.weekStart)} - ${formatMonDay(addDaysYmd(filters.weekStart, 6))}`
                : "Period: Typical week"
            }
          >
            <span className="aad__chip-label">Period</span>
            <span className="aad__chip-value">
              {filters.periodMode === PERIOD_SPECIFIC && filters.weekStart
                ? `${formatMonDay(filters.weekStart)} - ${formatMonDay(
                    addDaysYmd(filters.weekStart, 6)
                  )}`
                : "Typical week"}
            </span>
            <span className="aad__chip-caret" aria-hidden="true">
              {"▾"}
            </span>
          </button>
          {periodMenuOpen ? (
            <ul
              className="aad__period-menu"
              role="menu"
              data-testid="aad-period-menu"
            >
              <li>
                <button
                  type="button"
                  role="menuitem"
                  className={
                    "aad__period-menu-item" +
                    (filters.periodMode === PERIOD_TYPICAL
                      ? " aad__period-menu-item--active"
                      : "")
                  }
                  onClick={selectPeriodTypical}
                  data-testid="aad-period-typical"
                >
                  Typical week
                </button>
              </li>
              <li>
                <button
                  type="button"
                  role="menuitem"
                  className={
                    "aad__period-menu-item" +
                    (filters.periodMode === PERIOD_SPECIFIC
                      ? " aad__period-menu-item--active"
                      : "")
                  }
                  onClick={selectPeriodSpecific}
                  data-testid="aad-period-specific"
                >
                  Specific date range
                </button>
              </li>
            </ul>
          ) : null}
          {periodPopoverOpen ? (
            <div
              className="aad__period-popover"
              role="dialog"
              aria-label="Select week"
              data-testid="aad-period-popover"
            >
              <p className="aad__period-popover-title">Select a week</p>
              <label
                className="aad__period-popover-label"
                htmlFor="aad-period-week-start"
              >
                Week start (Sunday, MST)
              </label>
              <input
                id="aad-period-week-start"
                type="date"
                className="aad__period-popover-input"
                value={periodDraft.weekStart || ""}
                onChange={(e) => setDraftWeekStart(e.target.value)}
                data-testid="aad-period-week-start"
              />
              <p className="aad__period-popover-helper">
                {periodDraft.weekStart && YMD_PATTERN.test(periodDraft.weekStart)
                  ? `Showing ${formatMonDay(periodDraft.weekStart)} - ${formatMonDay(
                      addDaysYmd(periodDraft.weekStart, 6)
                    )}`
                  : "Pick a Sunday to anchor the week."}
              </p>
              <div className="aad__period-popover-presets">
                <button
                  type="button"
                  className="aad__period-popover-preset"
                  onClick={presetPrevWeek}
                  data-testid="aad-period-preset-prev"
                >
                  Previous week
                </button>
                <button
                  type="button"
                  className="aad__period-popover-preset"
                  onClick={presetThisWeek}
                  data-testid="aad-period-preset-this"
                >
                  This week
                </button>
                <button
                  type="button"
                  className="aad__period-popover-preset"
                  onClick={presetNextWeek}
                  data-testid="aad-period-preset-next"
                >
                  Next week
                </button>
              </div>
              {periodDraft.error ? (
                <p
                  className="aad__period-popover-error"
                  role="alert"
                  data-testid="aad-period-popover-error"
                >
                  {periodDraft.error}
                </p>
              ) : null}
              <div className="aad__period-popover-actions">
                <button
                  type="button"
                  className="aad__period-popover-cancel"
                  onClick={closePeriodPopover}
                  data-testid="aad-period-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="aad__period-popover-apply"
                  onClick={applyPeriodDraft}
                  disabled={!periodDraft.weekStart}
                  data-testid="aad-period-apply"
                >
                  Apply
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {/* SCHED-AVAIL-DASHBOARD-003-B Scenario 10/11 — mutually-exclusive
            with the "Only one-off windows" filter immediately below. The
            disabled state surfaces via aria-disabled + a tooltip; click is
            blocked by the disabled attribute itself. */}
        <label
          className={
            "aad__chip aad__chip--checkbox" +
            (filters.onlyNoAvailability ? " aad__chip--active" : "") +
            (filters.onlyOneOffWindows ? " aad__chip--disabled" : "")
          }
          title={
            filters.onlyOneOffWindows
              ? "Choose one filter at a time"
              : undefined
          }
        >
          <input
            type="checkbox"
            checked={filters.onlyNoAvailability}
            disabled={filters.onlyOneOffWindows}
            onChange={(e) => toggleOnlyNoAvailability(e.target.checked)}
            data-testid="aad-filter-only-no-availability"
            aria-label="Only show staff with no availability"
          />
          <span>Only show staff with no availability</span>
        </label>

        {/* SCHED-AVAIL-DASHBOARD-003-B Scenario 9/11 — new filter. Visible
            in every period mode; only meaningful when one-off rows are
            included in the data (Specific date range mode). In Typical
            mode it filters against in-data one-offs (which the BE may
            return as part of expired-recurring conversions or test data)
            — defense-in-depth, never throws. Mutually exclusive with
            "Only no availability". */}
        <label
          className={
            "aad__chip aad__chip--checkbox" +
            (filters.onlyOneOffWindows ? " aad__chip--active" : "") +
            (filters.onlyNoAvailability ? " aad__chip--disabled" : "")
          }
          title={
            filters.onlyNoAvailability
              ? "Choose one filter at a time"
              : undefined
          }
        >
          <input
            type="checkbox"
            checked={filters.onlyOneOffWindows}
            disabled={filters.onlyNoAvailability}
            onChange={(e) => toggleOnlyOneOffWindows(e.target.checked)}
            data-testid="aad-filter-only-one-off"
            aria-label="Only show staff with one-off windows"
          />
          <span>Only show staff with one-off windows</span>
        </label>

        <label className="aad__chip aad__chip--select">
          <span className="aad__chip-label">Sort</span>
          <select
            className="aad__chip-control"
            value={filters.sort}
            onChange={(e) => updateFilter({ sort: e.target.value })}
            data-testid="aad-filter-sort"
            aria-label="Sort staff rows"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        {/* SCHED-AVAIL-DASHBOARD-002-C — Period chevrons (Prev / Next week)
            visible in Specific date range mode only. Story Scenarios 5 + 7.
            Each chevron writes directly to filters.weekStart; the fetch
            effect picks up the change. Chevrons stay together as a small
            cluster for visual grouping with the Period chip. */}
        {filters.periodMode === PERIOD_SPECIFIC && (
          <div
            className="aad__period-chevrons"
            role="group"
            aria-label="Navigate weeks"
            data-testid="aad-period-chevrons"
          >
            <button
              type="button"
              className="aad__chevron-btn"
              onClick={shiftWeekPrev}
              aria-label="Previous week"
              title="Previous week"
              data-testid="aad-period-chevron-prev"
            >
              <FaChevronLeft aria-hidden="true" />
            </button>
            <button
              type="button"
              className="aad__chevron-btn"
              onClick={shiftWeekNext}
              aria-label="Next week"
              title="Next week"
              data-testid="aad-period-chevron-next"
            >
              <FaChevronRight aria-hidden="true" />
            </button>
          </div>
        )}

        {/* SCHED-AVAIL-DASHBOARD-002-C — Manual refresh button. Story
            Scenarios 2 + 7. Re-fires the current fetch without changing any
            filter so admin can pull fresh data when a parallel admin just
            edited availability. Disabled while a fetch is in flight to
            prevent double-clicks. */}
        <button
          type="button"
          className={
            "aad__refresh-btn" + (loading ? " aad__refresh-btn--loading" : "")
          }
          onClick={fetchDashboard}
          disabled={loading}
          aria-label="Refresh availability data"
          aria-busy={loading ? "true" : "false"}
          title="Refresh availability data"
          data-testid="aad-refresh-btn"
        >
          <FaSync
            aria-hidden="true"
            className={loading ? "aad__refresh-icon aad__refresh-icon--spin" : "aad__refresh-icon"}
          />
        </button>

        {filtersDirty && (
          <button
            type="button"
            className="aad__clear-filters"
            onClick={clearFilters}
            data-testid="aad-clear-filters"
          >
            Clear filters
          </button>
        )}

        {/* SCHED-AVAIL-DASHBOARD-003-B Scenario 1 — Bulk edit CTA. Anchored
            at the trailing edge of the strip via `margin-left: auto` on
            the wrapper. Opens the single-staff x N-windows bulk modal.
            Visible to admins (route-level RBAC already enforced; this
            CTA is defense-in-depth — story Scenario 15). */}
        <button
          type="button"
          className="aad__bulk-edit-btn"
          onClick={() => openBulkModal(null)}
          data-testid="aad-bulk-edit-btn"
          aria-label="Open bulk one-off availability authoring modal"
        >
          <FaPlus aria-hidden="true" className="aad__bulk-edit-icon" />
          <span>Bulk add one-off</span>
        </button>
      </div>

      {/* Matrix area --------------------------------------------------- */}
      {/* SCHED-AVAIL-DASHBOARD-002-C — loading rendering split:
          - Initial mount (first fetch, no prior data) → full skeleton
            replacement (Slice 1 behavior, no chrome jump because nothing
            else has rendered yet).
          - Subsequent fetches → overlay (semi-transparent layer + spinner
            ribbon) keeps the matrix + footer in place under it; chrome
            never flickers per story Scenarios 2 + 6. */}
      <main className="aad__matrix-area">
        {loading && !hasLoadedOnceRef.current ? (
          renderSkeleton()
        ) : error ? (
          renderErrorState()
        ) : staffRows.length === 0 ? (
          renderEmptyStateNoStaff()
        ) : visibleRows.length === 0 ? (
          renderEmptyStateNoMatch()
        ) : (
          <div className="aad__matrix-wrap">
            <div
              className={
                "aad__matrix-scroll" +
                (compact ? " aad__matrix-scroll--compact" : "")
              }
              aria-busy={loading ? "true" : "false"}
            >
              <table
                className="aad__matrix"
                aria-label="Staff availability matrix"
                data-testid="aad-matrix"
              >
                <thead>
                  <tr>
                    <th scope="col" className="aad__col-header aad__col-header--staff">
                      Staff
                    </th>
                    {DAY_HEADERS.map((label, idx) => (
                      <th
                        key={label}
                        scope="col"
                        className={
                          "aad__col-header aad__col-header--day" +
                          (idx === todayDow
                            ? " aad__col-header--today"
                            : "")
                        }
                        data-testid={`aad-col-header-${idx}`}
                        data-today={idx === todayDow ? "true" : undefined}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>{visibleRows.map(renderStaffRow)}</tbody>
              </table>
            </div>
            {loading && hasLoadedOnceRef.current ? (
              <div
                className="aad__matrix-overlay"
                role="status"
                aria-live="polite"
                aria-label="Refreshing staff availability"
                data-testid="aad-matrix-overlay"
              >
                <div className="aad__matrix-overlay-spinner" aria-hidden="true" />
              </div>
            ) : null}
            <p className="aad__footer-summary" data-testid="aad-footer-summary">
              {footerLabel}
            </p>
          </div>
        )}
      </main>

      {/* Drill-down modal — SCHED-1K reused verbatim ------------------- */}
      {modalState && (
        <AdminSetAvailabilityModal
          open={true}
          userId={modalState.userId}
          staffFullName={modalState.staffFullName}
          prefill={modalState.prefill}
          initialView={modalState.initialView}
          anchorAvailabilityId={modalState.anchorAvailabilityId}
          onClose={closeModal}
          onAvailabilityChanged={() => {
            // AC Scenario 13: matrix re-fetches on a successful mutation.
            // Modal owns its own close-on-success; we just kick the fetch.
            fetchDashboard();
          }}
        />
      )}

      {/* SCHED-AVAIL-DASHBOARD-003-B — Bulk one-off authoring modal --- */}
      <AdminBulkOneOffAvailabilityModal
        open={bulkModalOpen}
        onClose={closeBulkModal}
        onSubmitSuccess={onBulkSubmitSuccess}
        staffOptions={staffRows}
        preselectedUserId={bulkPreselectedUserId}
      />
    </div>
  );
};

export default AdminStaffAvailabilityDashboard;

// SCHED-AVAIL-DASHBOARD-002-C — pure helper for week-shift logic exposed for
// direct unit-test coverage. The component-internal shiftWeek closes over
// React state; this helper is the side-effect-free core of that logic.
const shiftWeekStartYmd = (currentYmd, days) => {
  const base =
    typeof currentYmd === "string" && YMD_PATTERN.test(currentYmd)
      ? currentYmd
      : phoenixWeekStartYmd();
  const next = addDaysYmd(base, days);
  return YMD_PATTERN.test(next) ? next : null;
};

// Exposed for unit tests — pure helpers exercised as units.
export const __internal = {
  DAY_KEYS,
  DAY_HEADERS,
  DAY_FULL,
  ROLE_PLURALIZE,
  humanRole,
  formatTimeCell,
  formatWindowRange,
  countEmptyDays,
  hasAnyAvailability,
  sortRows,
  isAnyFilterNonDefault,
  // SCHED-AVAIL-DASHBOARD-003-B
  rowHasAnyOneOff,
  DEFAULT_FILTERS,
  // Slice 2 helpers
  PERIOD_TYPICAL,
  PERIOD_SPECIFIC,
  formatMonDay,
  formatMonDayYear,
  ymdFromIso,
  phoenixWeekStartYmd,
  addDaysYmd,
  oneOffHint,
  orderWindowsForCell,
  countWindowKinds,
  ymdForDayInWeek,
  diffDaysYmd,
  // SCHED-AVAIL-DASHBOARD-002-C
  shiftWeekStartYmd,
};
